import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Mic2 } from 'lucide-react';
import type { Job } from '@portal/shared';
import {
  AppShell,
  Card,
  CardBody,
  Spinner,
  useToast,
} from '../../components/index.js';
import { downloadExcel, type Template } from '../../lib/stt-api.js';
import { api, ApiError } from '../../lib/api-client.js';
import { DictationUploader } from './dictation-uploader.js';

/**
 * Quick transcribe — standalone "no job" dictation flow. Buried from the
 * dashboard (Greg's main flow is inline on the job page) but kept for
 * one-off transcriptions and power-user direct links.
 *
 * If the URL still carries `?job=<id>` (legacy bookmark) we redirect to the
 * job's detail page — the inline uploader there is the preferred surface.
 */
export function STTTool() {
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('job');
  const navigate = useNavigate();
  const { toast } = useToast();

  const [template, setTemplate] = useState<Template>('going_concern');
  const [redirectingJob, setRedirectingJob] = useState<Job | null>(null);
  const [redirectError, setRedirectError] = useState<string | null>(null);

  // Legacy redirect: ?job=<id> → /tools/jobs/<id>
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ job: Job }>(`/api/jobs/${jobId}`);
        if (cancelled) return;
        setRedirectingJob(res.job);
        navigate(`/tools/jobs/${jobId}`, { replace: true });
      } catch (err) {
        if (cancelled) return;
        setRedirectError(err instanceof ApiError || err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId, navigate]);

  const downloadResult = useCallback(
    async ({ sttTaskId, firstFileName }: { sttTaskId: string; firstFileName: string }) => {
      const blob = await downloadExcel(sttTaskId);
      const baseName = firstFileName.replace(/\.[^.]+$/, '');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${baseName}-transcription.xlsx`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      a.remove();
      toast({ title: 'Downloaded transcription', variant: 'success' });
    },
    [toast],
  );

  if (jobId) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto px-8 py-10">
          <Card>
            <CardBody className="flex items-center gap-3 text-text-secondary">
              <Spinner size="sm" />
              <span>
                {redirectError
                  ? `Could not load job ${jobId}: ${redirectError}`
                  : redirectingJob
                    ? `Redirecting to ${redirectingJob.business}…`
                    : 'Redirecting…'}
              </span>
            </CardBody>
          </Card>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-8 py-10 space-y-6">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Back to dashboard
        </Link>

        <header className="flex items-center gap-3">
          <span className="inline-flex size-10 items-center justify-center rounded-lg bg-accent-muted text-accent-hover">
            <Mic2 className="size-5" aria-hidden />
          </span>
          <div>
            <h1 className="text-xl font-semibold text-text-primary leading-tight">
              Quick transcribe
            </h1>
            <p className="mt-0.5 text-sm text-text-secondary leading-tight">
              One-off recording → Excel. Not attached to a job. For job-scoped
              dictation, open the job and use the inline uploader.
            </p>
          </div>
        </header>

        <Card>
          <CardBody>
            <DictationUploader
              template={template}
              onTemplateChange={setTemplate}
              onComplete={downloadResult}
            />
          </CardBody>
        </Card>
      </div>
    </AppShell>
  );
}
